import { useState } from 'react';
import {
  Button,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { STATE_BUSINESS_SEARCH_URLS } from 'libs/ui/src/lib/components/borrowerBaseballCard/constants';
import {
  getStreetName,
  getStreetNumber,
  usdAmount,
} from 'libs/ui/src/lib/components/borrowerBaseballCard/utils';
import TrackRecordTitleHistory from 'libs/ui/src/lib/components/borrowerBaseballCard/trackRecordTitleHistory';

interface TrackRecordPropertyExpandedPanelProps {
  property: any;
  store: any;
  borrowerBaseballCardStore: any;
  isInternal: boolean;
  previewDocument: (id: number, name: string, fileExtension: string) => void;
}

const parseCustomLinks = (links: string) => {
  try {
    return JSON.parse(links) || [];
  } catch {
    return [];
  }
};

export const TrackRecordPropertyExpandedPanel = observer(({
  property,
  store,
  borrowerBaseballCardStore,
  isInternal,
  previewDocument,
}: TrackRecordPropertyExpandedPanelProps) => {
  const [isAddressLinksEditable, setIsAddressLinksEditable] = useState(false);
  const [linkKey, setLinkKey] = useState('');
  const [linkValue, setLinkValue] = useState('');
  const [titleHistoryOpen, setTitleHistoryOpen] = useState(false);

  const addressString = property.fullAddress
    ? property.fullAddress
    : `${property.purchaseAddress}, ${property.purchaseCity}, ${property.purchaseState}, ${property.purchaseZipcode}`;

  const googleHref = property.googleLink
    ? property.googleLink
    : `http://www.google.com/search?q=${property.purchaseAddress}, ${property.purchaseCity}, ${property.purchaseState}, ${property.purchaseZipcode}`;

  const zillowHref = property.zillowLink
    ? property.zillowLink
    : `http://www.zillow.com/homes/${property.purchaseAddress}, ${property.purchaseCity}, ${property.purchaseState}, ${property.purchaseZipcode}`.replace(/ /g, '-') + '_rb/';

  const lockedData = borrowerBaseballCardStore?.baseballCardDetails?.lockedData;

  const handleSaveLinks = async () => {
    const currentLinks = parseCustomLinks(property.links);

    if (linkKey && linkValue) {
      const links = [...currentLinks, { key: linkKey, value: linkValue }];
      await borrowerBaseballCardStore?.propertyFormStore?.editPropertyInformation({
        ...property,
        links: JSON.stringify(links),
      });
      runInAction(() => { property.links = JSON.stringify(links); });
      setLinkKey('');
      setLinkValue('');
    }
  };

  const handleDeleteLink = async (link: { key: string; value: string }) => {
    const newLinks = parseCustomLinks(property.links).filter(
      linkItr => !(linkItr.key === link.key && linkItr.value === link.value)
    );
    await borrowerBaseballCardStore?.propertyFormStore?.editPropertyInformation({
      ...property,
      links: JSON.stringify(newLinks),
    });
    runInAction(() => { property.links = JSON.stringify(newLinks); });
  };

  const openTitleHistory = async () => {
    if (!borrowerBaseballCardStore) return;
    const data = new FormData();
    const lookupRequest = {
      address: addressString,
      streetNumber: property.streetNumber
        ? property.streetNumber
        : getStreetNumber(property.purchaseAddress),
      streetName: property.streetName
        ? property.streetName
        : getStreetName(property.purchaseAddress),
      city: property.purchaseCity,
      state: property.purchaseState,
      zipCode: property.purchaseZipcode,
    };
    data.append('form_information', JSON.stringify(lookupRequest));
    const response = await borrowerBaseballCardStore?.lookupProperty?.(data);
    borrowerBaseballCardStore.currentCollateral = property;
    borrowerBaseballCardStore.collateralTitleHistory = response?.data?.rows;
    borrowerBaseballCardStore.titleHistoryMatGridStore.currentSelectedIndex = null;
    borrowerBaseballCardStore.titleHistoryMatGridStore.setSelectedRows([]);
    setTitleHistoryOpen(true);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} style={{ marginBottom: '3rem', display: 'flex', justifyContent: 'space-between' }}>
          <Grid>
            {isInternal && (
              <Grid style={{ marginRight: '4rem', marginBottom: '2rem' }}>
                <Typography variant="subtitle1">
                  <strong>Address Links</strong>
                  {!isAddressLinksEditable && !lockedData ? (
                    <IconButton
                      style={{ padding: 2 }}
                      onClick={() => setIsAddressLinksEditable(true)}
                    >
                      <EditIcon style={{ color: 'grey' }} />
                    </IconButton>
                  ) : null}
                </Typography>
                {isAddressLinksEditable && (
                  <Grid style={{ display: 'flex', marginBottom: '5px', marginTop: '5px' }}>
                    <TextField
                      label="Name"
                      value={linkKey}
                      onChange={(e) => setLinkKey(e.target.value)}
                    />
                    <TextField
                      style={{ marginLeft: '4px' }}
                      label="Link"
                      value={linkValue}
                      onChange={(e) => setLinkValue(e.target.value)}
                    />
                  </Grid>
                )}
                <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                  <Link href={googleHref} target="_blank" rel="noopener noreferrer">
                    Google
                  </Link>
                  <Link href={zillowHref} target="_blank" rel="noopener noreferrer">
                    Zillow
                  </Link>
                  <Link
                    component="button"
                    onClick={openTitleHistory}
                    style={{ alignSelf: 'flex-start', textAlign: 'left', padding: 0 }}
                  >
                    Title History
                  </Link>
                  <Link
                    href={`https://app.sigmacomputing.com/roc360/workbook/Surrounding-ROC-Loans-1bh5ddSdi8pvKcpF23eBgQ?Loan_ID=&Zip_Code=${property.purchaseZipcode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Surrounding Roc Loans
                  </Link>
                  <Link
                    href={STATE_BUSINESS_SEARCH_URLS[property?.purchaseState ? property.purchaseState : '']}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Secretary of State Site
                  </Link>
                  {parseCustomLinks(property.links).map((link, index) => (
                    <Grid key={index} style={{ display: 'flex', alignItems: 'center' }}>
                      <Link href={link.value} target="_blank" rel="noopener noreferrer">
                        {link.key}
                      </Link>
                      {isAddressLinksEditable && (
                        <IconButton style={{ padding: 0 }} onClick={() => handleDeleteLink(link)}>
                          <CloseIcon />
                        </IconButton>
                      )}
                    </Grid>
                  ))}
                </Grid>
                {isAddressLinksEditable ? (
                  <Grid
                    xs={12}
                    justifyContent="flex-end"
                    style={{ display: 'flex', padding: '1%' }}
                  >
                    <Button
                      onClick={() => {
                        setLinkKey('');
                        setLinkValue('');
                        setIsAddressLinksEditable(false);
                      }}
                      color="primary"
                    >
                      Close
                    </Button>
                    <Button
                      style={{ margin: 0 }}
                      onClick={handleSaveLinks}
                      variant="outlined"
                      color="primary"
                    >
                      Save
                    </Button>
                  </Grid>
                ) : null}
              </Grid>
            )}

            <Grid style={{ marginRight: '4rem' }}>
              <Typography variant="subtitle1">
                <strong>Documents</strong>
              </Typography>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {store.currentTasks && store.currentTasks.length > 0
                  ? store.currentTasks.map(task => {
                    if (task.originalFileName) {
                      return (
                        <Link
                          key={task.taskId}
                          component="button"
                          onClick={() => previewDocument(task.taskId, task.originalFileName, task.fileExtension)}
                        >
                          {task.originalFileName}
                        </Link>
                      );
                    }
                    return null;
                  })
                  : 'No Records'}
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <Grid style={{ marginRight: '4rem', marginBottom: '64px' }}>
              <strong>Mortgage Amount: </strong>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {property.mortgageAmount
                  ? usdAmount({ value: property.mortgageAmount })
                  : '$0.00'}
              </Grid>
            </Grid>
            <Grid style={{ marginRight: '4rem' }}>
              <strong>Verification Source: </strong>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {property.verificationSource ? property.verificationSource : 'Not Selected'}
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <Grid style={{ marginRight: '4rem', marginBottom: '64px' }}>
              <strong>Source Type: </strong>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {property.recordSourceType ? property.recordSourceType : 'None'}
              </Grid>
            </Grid>
            <Grid style={{ marginRight: '4rem' }}>
              <strong>Lender Name: </strong>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {property.lenderName ? property.lenderName : 'None'}
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <Grid style={{ marginRight: '4rem', marginBottom: '64px' }}>
              <strong>Property Type: </strong>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {property.propertyType ? property.propertyType : 'None'}
              </Grid>
            </Grid>
            <Grid style={{ marginRight: '4rem' }}>
              <strong>Deal Type: </strong>
              <Grid style={{ display: 'flex', flexDirection: 'column' }}>
                {property.dealType ? property.dealType : 'None'}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} style={{ marginBottom: '1rem' }}>
          <Typography variant="subtitle1">
            <strong>Comments</strong>
          </Typography>
          <Typography variant="body2">
            {property.comment && property.comment.trim() !== '' ? property.comment : 'None'}
          </Typography>
        </Grid>
      </Grid>

      <TrackRecordTitleHistory
        store={borrowerBaseballCardStore?.titleHistoryMatGridStore}
        property={property}
        titleHistory={borrowerBaseballCardStore?.collateralTitleHistory}
        open={titleHistoryOpen}
        setModalOpen={setTitleHistoryOpen}
        handleClose={() => {
          borrowerBaseballCardStore.collateralTitleHistory = null;
          setTitleHistoryOpen(false);
        }}
      />
    </>
  );
});
